import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useCan from "../../../../Hooks/useCan";
import { CompanyContext } from "../../../../Contexts/CompanyContext";
import useLoading from "../../../../Hooks/useLoading";
import useModal from "../../../../Hooks/useModal";
import { ProjectGroupingModel } from "../../../../Models/ProjectGroupingModel";
import { LinesModel } from "../../../../Models/LinesModel";
import { AuthContext } from "../../../../Contexts/AuthContext";
import { DeleteProjectGroupingLinesAPI, GetAllProjectLinesPaginatedByGroupingIdAPI } from "../../../../API/RecommendationAPI";
import { IResponseProps } from "../../../../Interfaces/IResponseProps";
import { AddJustificationProjectGroupingAPI, getGroupingByIdAPI } from "../../../../API/ProjectGroupingAPI";
import ModalLines from "../ModalLines";
import CardHeader from "../../../components/CardHeader";
import { AutoComplete, Button, Input, Space, Table, TableProps } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";

const ProjectGroupingLines = () => {
    const canView = useCan({ rolesCan: ["Manager", "Consultant", "Comercial", "Trainee", "Marketing"] });
    const canDelete = useCan({ rolesCan: ["Manager", "Consultant"] })
    const { id, projectGroupingId } = useParams();
    const { company } = useContext(CompanyContext);
    const { loading, toggleLoading } = useLoading();
    const { showModal, toggleShowModal } = useModal();
    const [projectGrouping, setProjectGrouping] = useState<ProjectGroupingModel>();
    const [lines, setLines] = useState<LinesModel[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [justification, setJustification] = useState<string>("");
    const [search, setSearch] = useState<string>("");
    const [valueHasChanged, setValueHasChanged] = useState<boolean>(false);
    const { userEmail } = useContext(AuthContext);
    const [take, setTake] = useState<number>(7);
    const navigate = useNavigate();

    const getAllLinesByProjectGrouping = async (page?: number) => {
        try {
            toggleLoading(true);

            let text = search == null || search == "" ? "all" : search;

            await GetAllProjectLinesPaginatedByGroupingIdAPI(projectGroupingId ?? "", page ?? 1, take, text).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {
                    setLines(resp.data.items);
                    setTotal(resp.data.total);
                }
            }).finally(() => {
                if (valueHasChanged) {
                    setValueHasChanged(false);
                }
                toggleLoading(false);
            });
        } catch (error: any) {
            toast.error(error);
            toggleLoading(false);
        }
    }

    const getProjectGrouping = async (projectGroupingId: string) => {
        try {
            toggleLoading(true);

            await getGroupingByIdAPI(projectGroupingId).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {
                    setProjectGrouping(resp.data);
                    setJustification(resp.data.justification);
                }
            }).finally(() => {
                toggleLoading(false);
            });
        } catch (error: any) {
            toggleLoading(false);
            toast.error(error);
        }
    }

    const putJustification = async () => {
        try {
            toggleLoading(true);

            await AddJustificationProjectGroupingAPI(projectGrouping?.id ?? "", justification, userEmail, company.id).finally(() => toggleLoading(false));
        } catch (error: any) {
            toast.error(error);
            toggleLoading(false);
        }
    }

    const deleteProjectLine = async (lineId: string) => {
        try {
            toggleLoading(true);

            await DeleteProjectGroupingLinesAPI(projectGroupingId ?? "", lineId, company.id).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {
                    getAllLinesByProjectGrouping();
                }
            }).finally(() => toggleLoading(false));
        } catch (error: any) {
            toggleLoading(false);
            toast.error(error);
        }
    }

    useEffect(() => {
        if (projectGroupingId) {
            getProjectGrouping(projectGroupingId).then(() => getAllLinesByProjectGrouping());
        }
    }, [projectGroupingId]);

    useEffect(() => {
        if (valueHasChanged) {
            getAllLinesByProjectGrouping();
        }
    }, [valueHasChanged, take]);

    function goToLine(id: string) {
        navigate(`/grantsSearcher/grants/${id}`);
    }

    const projectGroupingLinesColumns: TableProps<LinesModel>['columns'] = [
        {
            key: "favoritesGrantsName",
            dataIndex: "favoritesGrantsName",
            showSorterTooltip: { target: 'full-header' },
            filters: lines.map(item => ({
                text: item.favoritesGrantsName,
                value: item.favoritesGrantsName,
            })),
            title: "Nome da Linha",
            width: '50%'
        },
        {
            key: "favoritesGrantsName",
            dataIndex: "favoritesGrantsName",
            title: "Ações",
            width: '10%',
            render: (_, record) => (
                <Space size="small">
                    <Button type="text" size="small" onClick={() => goToLine(record.id)}><EyeOutlined className="blue" /></Button>
                </Space>
            ),
        },
    ];

    return (
        <div className='card ml-2 mr-2'>
            <ModalLines
                setValueHasChanged={setValueHasChanged}
                projectGroupingId={projectGroupingId ?? ""}
                show={showModal}
                toggleShow={toggleShowModal}
                companyId={company.id}
            />
            <CardHeader
                pageName={projectGrouping?.name ?? ""}
                hasPageBefore={true}
                pageBeforeName={"Recomendação"}
                linkPageBefore={`/recommendations/${id}`}
            />
            <div className='card-body mt-2 mb-2 w-100 h-100'>
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <AutoComplete
                        popupMatchSelectWidth={true}
                        className="w-100 pe-0"
                        onSearch={(val) => { setSearch(val) }}
                    >
                        <Input.Search onBlur={() => getAllLinesByProjectGrouping()} showCount={false} className="white" loading={loading} placeholder="Pesquisar por Linhas..." enterButton />
                    </AutoComplete>
                    {canView &&
                        <Button type="primary" color="primary" onClick={toggleShowModal}>
                            Adicionar Linhas
                        </Button>
                    }
                </div>
                <Table
                    loading={loading}
                    showHeader={false}
                    rowKey="id"
                    pagination={{
                        pageSizeOptions: ['7', '10', '25', '50', '75', '100'],
                        showSizeChanger: true,
                        total: total,
                        showTotal: (total) => `Total ${total} items`,
                        pageSize: take,
                        onChange: (page, pageSize) => {
                            getAllLinesByProjectGrouping(page)
                        },
                        onShowSizeChange: (page, size) => {
                            setTake(size)
                        }
                    }}
                    dataSource={lines}
                    columns={projectGroupingLinesColumns}
                />
                <div className="w-100 mt-2">
                    <label className="required mb-1">Justificativa FI </label>
                    <TextArea
                        value={justification}
                        disabled={!canView}
                        onChange={(e) => setJustification(e.target.value)}
                        className="form-control "
                        placeholder="Insira uma justificativa.."
                        onBlur={putJustification}
                        rows={4}
                        maxLength={undefined}
                    />
                </div>
            </div>
        </div>
    )
}

export default ProjectGroupingLines;